import React, { useState, useEffect } from 'react';
import { 
  Menu, X, ChevronRight, Monitor, LayoutTemplate, ShoppingCart, 
  Settings, Cloud, Wrench, CheckCircle2, MonitorSmartphone,
  Zap, RefreshCw, Server, MessageSquare, ShieldCheck, Mail, ExternalLink, Phone, Check
} from 'lucide-react';
import { initializeApp } from 'firebase/app';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getFirestore, collection, addDoc, serverTimestamp } from 'firebase/firestore';

// Safe environment variable helper
const getEnv = (key, fallback) => {
  try {
    if (typeof process !== 'undefined' && process.env && process.env[key]) {
      return process.env[key];
    }
  } catch (e) {}
  return fallback;
};

const firebaseConfig = {
  apiKey: getEnv('REACT_APP_FIREBASE_API_KEY', "AIzaSyDYZ6Tz4BUHCLq2BSrfj8dqF02CMv8eBDw"),
  authDomain: getEnv('REACT_APP_FIREBASE_AUTH_DOMAIN', "portfolio-khubaib.firebaseapp.com"),
  projectId: getEnv('REACT_APP_FIREBASE_PROJECT_ID', "portfolio-khubaib"),
  storageBucket: getEnv('REACT_APP_FIREBASE_STORAGE_BUCKET', "portfolio-khubaib.firebasestorage.app"),
  messagingSenderId: getEnv('REACT_APP_FIREBASE_MESSAGING_SENDER_ID', "417166028851"),
  appId: getEnv('REACT_APP_FIREBASE_APP_ID', "1:417166028851:web:310a183afc1931590c7346")
};

const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);

// 2D Intro Splash Screen with Smooth Upward Swipe Animation
const SplashScreen = ({ onFinish }) => {
  const [slideUp, setSlideUp] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setSlideUp(true), 1200);
    const finishTimer = setTimeout(onFinish, 1900);
    return () => {
      clearTimeout(timer);
      clearTimeout(finishTimer);
    };
  }, [onFinish]);

  return (
    <div className={`fixed inset-0 z-[200] bg-[#0b131f] flex flex-col items-center justify-center transition-all duration-700 ease-in-out ${slideUp ? '-translate-y-full opacity-0' : 'translate-y-0 opacity-100'}`}>
      <div className="flex flex-col items-center gap-4 transition-transform duration-700">
        <img 
          src="/kh-logo.png" 
          alt="KH Logo" 
          className="w-24 h-24 object-contain rounded-2xl border border-emerald-500/40 shadow-2xl animate-pulse"
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=150";
          }}
        />
        <div className="text-center">
          <h1 className="text-white font-extrabold text-xl tracking-widest">KHUBAIB HASSAN</h1>
          <p className="text-emerald-400 text-xs font-semibold tracking-wider mt-1">LOADING PORTFOLIO...</p>
        </div>
      </div>
    </div>
  );
};

const Navbar = ({ onOpenModal }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <nav className="fixed w-full z-50 top-0 bg-[#0b131f]/90 backdrop-blur-md border-b border-slate-800">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="flex justify-between items-center h-20">
          <div className="flex items-center gap-3">
            <img 
              src="/kh-logo.png" 
              alt="KH Logo" 
              className="w-10 h-10 object-contain rounded-lg border border-emerald-500/30"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=150";
              }}
            />
            <div>
              <span className="text-white font-bold text-base tracking-wide block">KHUBAIB HASSAN</span>
              <span className="text-slate-400 text-xs block">Web Developer & Website Manager</span>
            </div>
          </div>
          
          <div className="hidden md:flex items-center space-x-8 text-sm font-medium">
            <a href="#home" className="text-emerald-400 hover:text-emerald-300">Home</a>
            <a href="#services" className="text-slate-300 hover:text-white">Services</a>
            <a href="#work" className="text-slate-300 hover:text-white">Work</a>
            <a href="#about" className="text-slate-300 hover:text-white">About</a>
            <a href="#contact" className="text-slate-300 hover:text-white">Contact</a>
            <a 
              href="https://wa.me/923116227614" 
              target="_blank" 
              rel="noreferrer" 
              className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 px-6 py-2.5 rounded-full font-bold transition shadow-lg shadow-emerald-500/20"
            >
              Get In Touch
            </a>
          </div>

          <div className="md:hidden flex items-center">
            <button onClick={() => setIsOpen(!isOpen)} className="text-slate-300 hover:text-white">
              {isOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </div>

      {isOpen && (
        <div className="md:hidden bg-[#0b131f] border-b border-slate-800 px-6 py-4 space-y-3">
          <a href="#home" onClick={() => setIsOpen(false)} className="block text-emerald-400 font-medium">Home</a>
          <a href="#services" onClick={() => setIsOpen(false)} className="block text-slate-300 font-medium">Services</a>
          <a href="#work" onClick={() => setIsOpen(false)} className="block text-slate-300 font-medium">Work</a>
          <a href="#about" onClick={() => setIsOpen(false)} className="block text-slate-300 font-medium">About</a>
          <a href="#contact" onClick={() => setIsOpen(false)} className="block text-slate-300 font-medium">Contact</a>
          <a href="https://wa.me/923116227614" target="_blank" rel="noreferrer" className="block bg-emerald-500 text-slate-950 text-center py-2.5 rounded-full font-bold">
            Get In Touch (WhatsApp)
          </a>
        </div>
      )}
    </nav>
  );
};

const Hero = ({ onOpenModal }) => {
  return (
    <section id="home" className="pt-32 pb-20 bg-[#0b131f] min-h-screen flex items-center overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          <div className="space-y-6">
            <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold tracking-wider">
              BUILD • MANAGE • GROW
            </div>
            
            <h1 className="text-4xl sm:text-6xl font-extrabold text-white leading-tight">
              Websites that make businesses look better and <span className="text-emerald-400">work smarter.</span>
            </h1>
            
            <p className="text-slate-400 text-lg leading-relaxed">
              I build modern, responsive and easy-to-manage websites that help businesses grow online. From development to ongoing management, I've got you covered.
            </p>
            
            <div className="flex flex-wrap gap-4 pt-2">
              <a href="#work" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold px-7 py-3.5 rounded-full transition shadow-lg shadow-emerald-500/20 flex items-center gap-2">
                View My Work <ChevronRight size={18} />
              </a>
              <a href="https://wa.me/923116227614" target="_blank" rel="noreferrer" className="border border-slate-700 hover:border-emerald-500 text-white font-bold px-7 py-3.5 rounded-full transition flex items-center gap-2">
                Let's Talk
              </a>
            </div>

            {/* Tech Stack using public folder logos */}
            <div className="flex flex-wrap items-center gap-6 pt-6 border-t border-slate-800 text-slate-300 text-sm font-medium">
              <span className="flex items-center gap-2">
                <img src="/react.png" alt="React" className="w-5 h-5 object-contain" onError={(e)=>{e.target.src="https://raw.githubusercontent.com/github/explore/80688e429a7d4ef2fca1e82350fe8e3517d3494d/topics/react/react.png"}} /> React
              </span>
              <span className="flex items-center gap-2">
                <img src="/tailwind.png" alt="Tailwind" className="w-5 h-5 object-contain" onError={(e)=>{e.target.src="https://www.vectorlogo.zone/logos/tailwindcss/tailwindcss-icon.svg"}} /> Tailwind CSS
              </span>
              <span className="flex items-center gap-2">
                <img src="/firebase.png" alt="Firebase" className="w-5 h-5 object-contain" onError={(e)=>{e.target.src="https://www.vectorlogo.zone/logos/firebase/firebase-icon.svg"}} /> Firebase
              </span>
              <span className="flex items-center gap-2">
                <img src="/wordpress.png" alt="WordPress" className="w-5 h-5 object-contain" onError={(e)=>{e.target.src="https://www.vectorlogo.zone/logos/wordpress/wordpress-icon.svg"}} /> WordPress
              </span>
            </div>
          </div>

          {/* Realistic Device Mockups (Laptop + Mobile) */}
          <div className="relative flex items-center justify-center pt-6 lg:pt-0">
            <div className="relative w-full max-w-xl">
              
              {/* Laptop Frame (Chaaye & Grills) */}
              <div className="bg-slate-900 border-[6px] border-slate-700 rounded-t-2xl shadow-2xl overflow-hidden aspect-[16/10] relative">
                <div className="bg-black w-full h-full flex flex-col">
                  <div className="bg-slate-800 px-4 py-2 flex items-center justify-between text-[10px] text-slate-400 border-b border-slate-700">
                    <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500"></div><div className="w-2.5 h-2.5 rounded-full bg-yellow-500"></div><div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div></div>
                    <span>chaiandgrill.com</span>
                    <span>🔗 Live</span>
                  </div>
                  <div className="flex-1 relative overflow-hidden bg-black">
                    <img 
                      src="/chaaye.jpg" 
                      alt="Chaaye & Grills" 
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1555396273-367ea4eb4db5?auto=format&fit=crop&w=1000&q=80";
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="bg-slate-700 h-4 rounded-b-xl w-[110%] -ml-[5%] shadow-lg flex justify-center items-center">
                <div className="w-20 h-1 bg-slate-500 rounded-full"></div>
              </div>

              {/* Floating Mobile Phone Mockup (Medi-Track HMS) */}
              <div className="absolute -right-4 -bottom-6 w-32 sm:w-40 bg-slate-950 border-[5px] border-slate-700 rounded-[30px] shadow-2xl overflow-hidden hidden sm:block">
                <div className="bg-black p-1.5 flex flex-col items-center">
                  <div className="w-12 h-2.5 bg-slate-800 rounded-full mb-1.5"></div>
                  <div className="bg-slate-900 w-full rounded-2xl overflow-hidden h-52 relative border border-slate-800">
                    <img 
                      src="/meditrack.jpg" 
                      alt="Medi-Track HMS Mobile" 
                      className="w-full h-full object-cover object-top"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = "https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&w=600&q=80";
                      }}
                    />
                  </div>
                </div>
              </div>

            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const WhatIDo = () => {
  const services = [
    { icon: <Monitor size={24} className="text-slate-800" />, title: "Website Development", desc: "Modern, responsive and custom websites." },
    { icon: <LayoutTemplate size={24} className="text-slate-800" />, title: "Website Redesign", desc: "Give your existing site a fresh, modern look." },
    { icon: <ShoppingCart size={24} className="text-slate-800" />, title: "E-commerce Websites", desc: "Turn your products into online sales." },
    { icon: <Settings size={24} className="text-slate-800" />, title: "Business Website Setup", desc: "Professionally showcase your business." },
    { icon: <Cloud size={24} className="text-slate-800" />, title: "Hosting & Domain Management", desc: "Secure and reliable hosting with domain setup." },
    { icon: <Wrench size={24} className="text-slate-800" />, title: "Website Maintenance", desc: "Updates, bug fixes and performance optimization." },
  ];

  return (
    <section id="services" className="py-24 bg-white text-slate-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          
          <div>
            <span className="text-emerald-600 font-bold tracking-wider text-xs uppercase block mb-2">WHAT I DO</span>
            <h2 className="text-4xl font-extrabold text-slate-900 mb-4">Your Complete Web Solution</h2>
            <p className="text-slate-600 text-base leading-relaxed">
              I offer end-to-end website services — from building your online presence to keeping it running smoothly.
            </p>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
            {services.map((svc, idx) => (
              <div key={idx} className="bg-slate-50 border border-slate-200/80 p-8 rounded-2xl shadow-sm hover:shadow-md transition">
                <div className="mb-4">{svc.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">{svc.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{svc.desc}</p>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

const SelectedProjects = () => {
  const projects = [
    {
      title: "Chaaye & Grills (Restaurant)",
      desc: "A modern website for a restaurant with menu, online ordering and a clean design.",
      tags: ["React", "Tailwind CSS", "Firebase"],
      url: "https://chaiandgrill.com/",
      image: "/chaaye.jpg"
    },
    {
      title: "Perfume Store (E-commerce)",
      desc: "A sleek online store with product catalog, cart and secure checkout.",
      tags: ["React", "Stripe", "Firebase"],
      url: "#",
      image: "https://images.unsplash.com/photo-1523293182086-7651a899d37f?auto=format&fit=crop&w=800&q=80"
    },
    {
      title: "Business Website (Medi-Track HMS)",
      desc: "Professional business website with services, portfolio and contact form.",
      tags: ["WordPress", "Elementor", "Hosting"],
      url: "https://medi-track-11dcb.web.app/login",
      image: "/meditrack.jpg"
    }
  ];

  return (
    <section id="work" className="py-24 bg-[#0b131f] text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-12 items-start">
          
          <div>
            <span className="text-emerald-400 font-bold tracking-wider text-xs uppercase block mb-2">MY WORK</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">Selected Projects</h2>
            <p className="text-slate-400 text-sm leading-relaxed mb-6">
              Real businesses. Real websites. Here are a few projects I've built and managed for clients.
            </p>
            <a href="https://chaiandgrill.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 border border-slate-700 hover:border-emerald-500 text-white font-semibold text-sm px-6 py-3 rounded-full transition">
              View All Projects →
            </a>
          </div>

          <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-6">
            {projects.map((proj, idx) => (
              <div key={idx} className="bg-white text-slate-900 rounded-2xl overflow-hidden shadow-xl flex flex-col justify-between group">
                <div className="h-44 w-full relative overflow-hidden bg-slate-900 border-b border-slate-200">
                  <img src={proj.image} alt={proj.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6 flex-1 flex flex-col justify-between">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900 mb-2">{proj.title}</h3>
                    <p className="text-slate-600 text-xs leading-relaxed mb-4">{proj.desc}</p>
                    <div className="flex flex-wrap gap-1.5 mb-6">
                      {proj.tags.map(t => (
                        <span key={t} className="text-[11px] bg-slate-100 text-slate-700 font-medium px-2.5 py-1 rounded">{t}</span>
                      ))}
                    </div>
                  </div>
                  <a href={proj.url} target="_blank" rel="noreferrer" className="text-emerald-600 hover:text-emerald-700 font-bold text-sm inline-flex items-center gap-1">
                    View Case Study →
                  </a>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

const CaseStudy = () => {
  return (
    <section className="py-24 bg-slate-100 text-slate-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="mb-12">
          <span className="text-emerald-600 font-bold tracking-wider text-xs uppercase block mb-2">CASE STUDY</span>
          <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">Chaaye & Grills – Restaurant Website</h2>
          <p className="text-slate-600 text-base">A modern, fast and user-friendly website to showcase the menu, attract more customers and make ordering easy.</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Client</span>
              <p className="font-bold text-slate-900">Local Restaurant</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Key Features</span>
              <ul className="text-xs space-y-1.5 text-slate-700 font-medium">
                <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600"/> Responsive design</li>
                <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600"/> Digital menu</li>
                <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600"/> Online order button</li>
                <li className="flex items-center gap-1.5"><Check size={14} className="text-emerald-600"/> Mobile friendly</li>
              </ul>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">What I Built</span>
              <p className="text-xs text-slate-700 font-medium">Full website with menu, online ordering and contact system.</p>
            </div>
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
              <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Result</span>
              <p className="text-xs text-slate-700 font-medium">Increased online presence and more customer engagement.</p>
            </div>
            <div className="sm:col-span-2 bg-white p-6 rounded-2xl shadow-sm border border-slate-200 flex justify-between items-center">
              <div>
                <span className="text-xs font-bold text-slate-400 uppercase tracking-wider block mb-1">Technologies</span>
                <p className="text-xs text-slate-900 font-bold">React, Tailwind CSS, Firebase</p>
              </div>
              <a href="https://chaiandgrill.com/" target="_blank" rel="noreferrer" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs px-5 py-2.5 rounded-full shadow transition">
                View Live Site →
              </a>
            </div>
          </div>

          <div className="relative flex justify-center">
            <div className="bg-slate-900 rounded-2xl p-3 shadow-2xl border border-slate-800 w-full max-w-lg overflow-hidden">
              <img src="/chaaye.jpg" alt="Chaaye & Grills Case Study" className="w-full h-auto rounded-lg object-cover" />
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const WhyWorkWithMe = () => {
  const features = [
    { title: "Mobile-First Design", desc: "Looks great on every device." },
    { title: "Fast & Modern", desc: "Clean code, optimized performance." },
    { title: "Easy Content Updates", desc: "You can manage it yourself (or I can)." },
    { title: "Reliable Hosting", desc: "99.9% uptime & security." },
    { title: "Ongoing Maintenance", desc: "Keep your site running perfectly." },
    { title: "Clear Communication", desc: "No jargon. Just honest and timely updates." },
  ];

  return (
    <section className="py-24 bg-[#0b131f] text-white">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">
          
          <div>
            <span className="text-emerald-400 font-bold tracking-wider text-xs uppercase block mb-2">WHY WORK WITH ME</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-white mb-4">A Partner for Your Online Growth</h2>
            <p className="text-slate-400 text-sm leading-relaxed">
              I focus on quality, reliability and your business goals. Here's what sets me apart:
            </p>
          </div>

          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-10">
            {features.map((feat, idx) => (
              <div key={idx} className="flex gap-4">
                <div className="w-10 h-10 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center text-emerald-400 shrink-0 font-bold">✓</div>
                <div>
                  <h4 className="font-bold text-base text-white mb-1">{feat.title}</h4>
                  <p className="text-slate-400 text-xs leading-relaxed">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </div>
    </section>
  );
};

const WebsiteManagement = () => {
  return (
    <section className="py-20 bg-slate-100 text-slate-900 border-t border-slate-200">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-center bg-white border border-slate-200 rounded-3xl p-8 sm:p-12 shadow-sm">
          
          <div>
            <span className="text-emerald-600 font-bold tracking-wider text-xs uppercase block mb-2">WEBSITE MANAGEMENT</span>
            <h2 className="text-3xl font-extrabold text-slate-900 mb-3">Keep Your Website Running</h2>
            <p className="text-slate-600 text-sm leading-relaxed">I don't just build websites — I help you maintain and grow them.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs font-semibold text-slate-700">
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><CheckCircle2 size={16} className="text-emerald-600"/> Content & menu updates</div>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><CheckCircle2 size={16} className="text-emerald-600"/> Security & backup checks</div>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><CheckCircle2 size={16} className="text-emerald-600"/> Domain & hosting management</div>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><CheckCircle2 size={16} className="text-emerald-600"/> New features & improvements</div>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><CheckCircle2 size={16} className="text-emerald-600"/> Bug fixes & troubleshooting</div>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100"><CheckCircle2 size={16} className="text-emerald-600"/> Regular reporting</div>
            <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-100 sm:col-span-2"><CheckCircle2 size={16} className="text-emerald-600"/> Performance optimization & ongoing support</div>
          </div>

          <div className="bg-[#0b131f] text-white p-6 rounded-2xl flex flex-col justify-center text-center">
            <p className="font-bold text-sm mb-1">Focus on your business.</p>
            <p className="text-emerald-400 font-bold text-sm">I'll take care of your website.</p>
          </div>

        </div>
      </div>
    </section>
  );
};

const AboutAndContact = () => {
  return (
    <section id="about" className="py-24 bg-white text-slate-900">
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          
          <div className="space-y-6">
            <span className="text-emerald-600 font-bold tracking-wider text-xs uppercase block">ABOUT ME</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900">Hi, I'm Khubaib Hassan</h2>
            <p className="text-slate-600 text-base leading-relaxed">
              I'm a passionate web developer with a focus on building useful, beautiful and reliable websites for businesses.
            </p>
            <p className="text-slate-600 text-base leading-relaxed">
              I love turning ideas into real online solutions and helping businesses grow in the digital world.
            </p>

            <div className="flex flex-wrap gap-3 pt-2">
              <span className="bg-slate-100 text-slate-800 text-xs font-bold px-4 py-2 rounded-lg">Web Development</span>
              <span className="bg-slate-100 text-slate-800 text-xs font-bold px-4 py-2 rounded-lg">Website Management</span>
              <span className="bg-slate-100 text-slate-800 text-xs font-bold px-4 py-2 rounded-lg">Problem Solver</span>
            </div>
          </div>

          <div id="contact" className="bg-slate-50 border border-slate-200 p-8 sm:p-10 rounded-3xl shadow-sm">
            <span className="text-emerald-600 font-bold tracking-wider text-xs uppercase block mb-1">LET'S TALK</span>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mb-3">Have a business that needs a better website?</h2>
            <p className="text-slate-600 text-sm mb-8">I'd love to hear about your project and how I can help.</p>
            
            <div className="flex flex-wrap gap-4">
              <a 
                href="https://wa.me/923116227614"
                target="_blank"
                rel="noreferrer"
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-7 py-3.5 rounded-full transition shadow-lg shadow-emerald-600/20 text-sm flex items-center gap-2"
              >
                Get In Touch <ChevronRight size={16} />
              </a>
              <a 
                href="mailto:khubaib20103@gmail.com"
                className="border border-slate-300 hover:bg-slate-100 text-slate-800 font-bold px-7 py-3.5 rounded-full transition text-sm flex items-center gap-2"
              >
                <Mail size={16} /> Email Me
              </a>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

const ContactModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({ name: '', phone: '', email: '', message: '' });
  const [status, setStatus] = useState('');

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('Sending...');
    try {
      await addDoc(collection(db, 'messages'), {
        ...formData,
        createdAt: serverTimestamp()
      });
      setStatus('Message sent successfully!');
      setFormData({ name: '', phone: '', email: '', message: '' });
      setTimeout(() => {
        setStatus('');
        onClose();
      }, 2000);
    } catch (err) {
      console.error(err);
      setStatus('Failed to send. Please try WhatsApp instead.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-[#0b131f] border border-slate-800 rounded-2xl w-full max-w-md p-8 relative shadow-2xl text-white">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-white">
          <X size={20} />
        </button>
        <h3 className="text-2xl font-bold mb-1">Get In Touch</h3>
        <p className="text-slate-400 text-xs mb-6">Fill out the form below or chat directly on WhatsApp: 03116227614</p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold uppercase text-slate-400 mb-1">Your Name</label>
            <input 
              type="text" 
              required 
              value={formData.name} 
              onChange={(e) => setFormData({...formData, name: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:border-emerald-500 outline-none"
              placeholder="Enter your name" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-400 mb-1">Phone Number</label>
            <input 
              type="text" 
              required 
              value={formData.phone} 
              onChange={(e) => setFormData({...formData, phone: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:border-emerald-500 outline-none"
              placeholder="Enter your phone" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-400 mb-1">Email</label>
            <input 
              type="email" 
              value={formData.email} 
              onChange={(e) => setFormData({...formData, email: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:border-emerald-500 outline-none"
              placeholder="Enter your email" 
            />
          </div>
          <div>
            <label className="block text-xs font-bold uppercase text-slate-400 mb-1">Message</label>
            <textarea 
              rows="3" 
              value={formData.message} 
              onChange={(e) => setFormData({...formData, message: e.target.value})}
              className="w-full bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 text-sm focus:border-emerald-500 outline-none"
              placeholder="How can I help you?"
            ></textarea>
          </div>
          <button 
            type="submit" 
            className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3.5 rounded-xl transition shadow-lg"
          >
            Send Message
          </button>
          {status && <p className="text-center text-xs text-emerald-400 mt-2">{status}</p>}
        </form>
      </div>
    </div>
  );
};

const Footer = () => {
  return (
    <footer className="bg-[#070c14] text-slate-500 py-10 border-t border-slate-800 text-xs">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <div className="flex items-center gap-3">
          <img 
            src="/kh-logo.png" 
            alt="KH Logo" 
            className="w-8 h-8 object-contain rounded border border-emerald-500/30"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=100";
            }}
          />
          <div>
            <span className="text-slate-300 font-bold">Khubaib Hassan</span>
            <p className="text-slate-500">Better Websites • Stronger Businesses</p>
          </div>
        </div>
        <div className="flex gap-6 text-slate-400">
          <a href="https://wa.me/923116227614" target="_blank" rel="noreferrer" className="hover:text-emerald-400">WhatsApp: 03116227614</a>
          <a href="mailto:khubaib20103@gmail.com" className="hover:text-emerald-400">Email: khubaib20103@gmail.com</a>
        </div>
        <p>© 2026 Khubaib Hassan. All rights reserved.</p>
      </div>
    </footer>
  );
};

export default function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    signInAnonymously(auth).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0b131f] text-slate-100 font-sans selection:bg-emerald-500 selection:text-slate-950">
      {isLoading && <SplashScreen onFinish={() => setIsLoading(false)} />}
      <Navbar onOpenModal={() => setIsModalOpen(true)} />
      <Hero onOpenModal={() => setIsModalOpen(true)} />
      <WhatIDo />
      <SelectedProjects />
      <CaseStudy />
      <WhyWorkWithMe />
      <WebsiteManagement />
      <AboutAndContact />
      <Footer />
      <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </div>
  );
}